import React, { useState, useEffect } from 'react';
import { Coffee, ShoppingCart } from 'lucide-react';
import ProductCard from './ProductCard';
import { getGridClasses } from '../services/themeService';
import { getRandomSlogan } from '../data/themes';

const MenuPreview = ({ currentTheme, customization, shopName = "Your Coffee Shop" }) => {
  const [theme, setTheme] = useState(currentTheme);
  const [custom, setCustom] = useState(customization);
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Sample menu data for preview using actual available images
  const sampleProducts = [
    {
      id: 1,
      name: 'Americano',
      description: 'Rich and bold espresso with hot water',
      price: 3.50,
      category: 'Coffee',
      imageUrl: '/api/assets/products/americano.jpg',
      available: true
    },
    {
      id: 2,
      name: 'Latte',
      description: 'Smooth espresso with steamed milk',
      price: 4.25,
      category: 'Coffee',
      imageUrl: '/api/assets/products/latte.jpg',
      available: true
    },
    {
      id: 3,
      name: 'Cappuccino',
      description: 'Perfect balance of espresso, steamed milk and foam',
      price: 4.00,
      category: 'Coffee',
      imageUrl: '/api/assets/products/cappuccino.jpg',
      available: true
    },
    {
      id: 4,
      name: 'Iced Americano',
      description: 'Cold espresso with water and ice',
      price: 3.75,
      category: 'Iced Drinks',
      imageUrl: '/api/assets/products/iced-americano.jpg',
      available: true
    },
    {
      id: 5,
      name: 'Iced Latte',
      description: 'Refreshing cold latte with ice',
      price: 4.50,
      category: 'Iced Drinks',
      imageUrl: '/api/assets/products/iced-latte.jpg',
      available: true
    },
    {
      id: 6,
      name: 'Iced Mocha',
      description: 'Cold chocolate coffee delight',
      price: 5.00,
      category: 'Iced Drinks',
      imageUrl: '/api/assets/products/iced-mocha.jpg',
      available: true
    },
    {
      id: 7,
      name: 'Croissant',
      description: 'Buttery and flaky French pastry',
      price: 3.25,
      category: 'Food',
      imageUrl: '/api/assets/products/croissant.jpg',
      available: true
    },
    {
      id: 8,
      name: 'Blueberry Muffin',
      description: 'Fresh baked muffin with blueberries',
      price: 3.50,
      category: 'Food',
      imageUrl: '/api/assets/products/muffin-blueberry.jpg',
      available: true
    }
  ];

  useEffect(() => {
    setTheme(currentTheme);
    setCustom(customization);
  }, [currentTheme, customization]);

  const categories = ['all', ...new Set(sampleProducts.map(product => product.category))];

  const filteredProducts = selectedCategory === 'all' 
    ? sampleProducts 
    : sampleProducts.filter(product => product.category === selectedCategory);

  const renderFeaturedLayout = () => {
    const featuredProduct = filteredProducts[0];
    const otherProducts = filteredProducts.slice(1);
    
    return (
      <div className="space-y-6">
        {featuredProduct && (
          <ProductCard
            key={featuredProduct.id}
            product={featuredProduct}
            theme={theme}
            customization={theme?.layout || custom}
            isPreview={true}
            isFeatured={true}
          />
        )}
        <div className="grid grid-cols-2 gap-4">
          {otherProducts.slice(0, 4).map(product => (
            <ProductCard
              key={product.id}
              product={product}
              theme={theme}
              customization={{...theme?.layout || custom, productCardStyle: 'vertical'}}
              isPreview={true}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div 
      className="h-full overflow-auto"
      style={{ 
        background: theme.colors.background.includes('gradient') ? theme.colors.background : theme.colors.background,
        fontFamily: theme.typography.fontFamily
      }}
    >
      {/* Beautiful Header with Shop Name and Slogan */}
      <header 
        className="relative overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${theme.colors.primary}, ${theme.colors.secondary})`,
        }}
      >
        {/* Background Pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='60' height='60' viewBox='0 0 60 60'%3E%3Cg fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
        
        <div className="relative px-4 py-8">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div 
                className="p-4 rounded-full"
                style={{ backgroundColor: `${theme.colors.surface}20` }}
              >
                <Coffee 
                  className="w-12 h-12" 
                  style={{ color: theme.colors.surface }}
                />
              </div>
            </div>
            
            <h1 
              className="font-bold leading-tight mb-2"
              style={{ 
                color: theme.colors.surface,
                fontFamily: theme.typography.headingFont,
                fontSize: `${theme.typography.headingSize + 8}px`
              }}
            >
{shopName}
            </h1>
            
            <p 
              className="text-lg mb-4 opacity-90"
              style={{ 
                color: theme.colors.surface,
                fontFamily: theme.typography.bodyFont
              }}
            >
{getRandomSlogan()}
            </p>
            
            <div 
              className="inline-flex items-center space-x-3 px-6 py-3 rounded-full"
              style={{ 
                backgroundColor: `${theme.colors.surface}20`,
                backdropFilter: 'blur(10px)'
              }}
            >
              <span 
                className="text-sm font-medium"
                style={{ 
                  color: theme.colors.surface,
                  fontFamily: theme.typography.bodyFont
                }}
              >
                Table 1 • Live Preview
              </span>
              
              <div 
                className="flex items-center space-x-2 px-3 py-2 rounded-full"
                style={{ 
                  backgroundColor: theme.colors.accent,
                  color: theme.colors.surface
                }}
              >
                <ShoppingCart className="w-4 h-4" />
                <span className="text-sm font-bold">0</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Category Filter - Same as ShopMenu */}
      {(theme?.layout?.showCategories !== undefined ? theme.layout.showCategories : (custom?.showCategories !== undefined ? custom.showCategories : true)) && (
        <div className="px-4 py-6">
          <div className="flex space-x-3 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => {
              const count = category === 'all' 
                ? sampleProducts.length
                : sampleProducts.filter(p => p.category === category).length;
              
              return (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className="flex items-center space-x-2 px-6 py-3 rounded-2xl font-bold text-lg whitespace-nowrap transition-all duration-200 hover:scale-105"
                  style={{
                    backgroundColor: selectedCategory === category ? theme.colors.primary : theme.colors.surface,
                    color: selectedCategory === category ? theme.colors.text : theme.colors.textSecondary,
                    fontFamily: theme.typography.headingFont
                  }}
                >
                  <span>{category === 'all' ? 'All' : category}</span>
                  <span 
                    className="px-2 py-1 rounded-full text-sm"
                    style={{ 
                      backgroundColor: selectedCategory === category ? theme.colors.surface : theme.colors.border,
                      color: selectedCategory === category ? theme.colors.textSecondary : theme.colors.text
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Products Grid - Same as ShopMenu */}
      <div className="px-4 pb-8">
        {theme?.layout?.layoutStyle === 'featured' ? (
          renderFeaturedLayout()
        ) : (
          <div 
            className={`gap-6 ${getGridClasses(theme, custom)}`}
          >
            {filteredProducts.map(product => (
              <ProductCard
                key={product.id}
                product={product}
                theme={theme}
                customization={theme?.layout || custom}
                isPreview={true}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MenuPreview;
